/* This client's build trigger and cache refresh URL — Triton staff only.
 *
 * Their gallery route renders on demand and is always current, but the rest of
 * their site is prerendered — so a service page pulling album photos into a
 * section only catches up when the site builds. That is what the hook is for.
 *
 * A DNN site is the other kind: it renders every page from the API and keeps a
 * copy for a few minutes, so it needs its cache dropped rather than a build.
 * The TritonIQ DNN Gallery module shows the URL to paste (with its secret) in
 * its own settings, under Connection.
 *
 * There was a layout setting here too, choosing between one gallery and several.
 * A client has one, always: they organise with albums and sub-albums inside it
 * and are never shown the container. Two shapes meant two sets of screens, and
 * the one nobody wanted was where the bugs lived.
 *
 * The role check is presentation only — the endpoints enforce the same rule (see
 * `adminOnly` in worker/gallerystudio.ts).
 */
import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Input } from '@/components/ui';
import { Icon } from '@/lib/icons';
import { useIsAdmin } from '@/auth/useIsAdmin';
import { getGallerySettings, updateGallerySettings } from '@/data/client';
import { Modal } from './Modal';
import { Hint } from './Hint';

/* "3 minutes ago" beats an ISO string here: the only question an admin is asking
   is whether the last build was before or after the change they just made. */
function ago(iso: string): string {
  const secs = Math.max(0, (Date.now() - Date.parse(iso)) / 1000);
  if (!Number.isFinite(secs)) return '';
  if (secs < 90) return 'just now';
  const mins = Math.round(secs / 60);
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.round(mins / 60);
  return hrs < 48 ? `${hrs} hr ago` : `${Math.round(hrs / 24)} days ago`;
}

function SettingsDialog({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ['gallerySettings'], queryFn: getGallerySettings });
  const [err, setErr] = useState('');

  const save = useMutation({
    mutationFn: updateGallerySettings,
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['gallerySettings'] });
      await qc.invalidateQueries({ queryKey: ['galleries'] });
      setErr('');
    },
    onError: (e) => setErr(e instanceof Error ? e.message.replace(/^API \d+:\s*/, '') : 'Could not save.'),
  });

  /* Local while typing, reconciled when the server answers — a controlled input
     bound straight to query data fights the user on every refetch. */
  const [limit, setLimit] = useState('6');
  useEffect(() => { if (data) setLimit(String(data.featuredLimit)); }, [data?.featuredLimit]);
  const [hook, setHook] = useState('');
  useEffect(() => { if (data) setHook(data.deployHookUrl); }, [data?.deployHookUrl]);
  const [cacheHook, setCacheHook] = useState('');
  useEffect(() => { if (data) setCacheHook(data.cacheHookUrl); }, [data?.cacheHookUrl]);

  return (
    <Modal title="Gallery settings" onClose={onClose}>
      <div className="es-modal__body">
        {isLoading && <p className="es-hint">Loading…</p>}
        {data && (
          <>
            <span className="es-label">
              Featured photos
              <Hint label="What does featuring do?">
                A featured photo is flagged in the gallery API so this client's
                site can use it somewhere that is not the gallery &mdash; a homepage
                strip, a band on a service page. What their template does with
                it is up to the template; this only sets how many they can pick.
                Six suits most, which lay out 2, 3 or 6 across. Lowering the
                number never unfeatures anything.
              </Hint>
            </span>
            <div className="es-actionrow">
              <Input type="number" min={1} max={50} value={limit}
                onChange={(e) => setLimit(e.target.value)}
                onBlur={() => {
                  const n = Math.round(Number(limit));
                  if (Number.isFinite(n) && n >= 1 && n <= 50 && n !== data.featuredLimit) save.mutate({ featuredLimit: n });
                  else setLimit(String(data.featuredLimit));
                }} />
            </div>
            <span className="es-hint">Between 1 and 50.</span>

            <span className="es-label">
              Rebuild hook
              <Hint label="What is this for?">
                This client's gallery page is current the moment they save. Their
                other pages are prerendered, so anything pulling album photos
                into a section only updates when the site rebuilds &mdash; paste
                their deploy hook and saving triggers one. Leave it empty and
                nothing is triggered, and their gallery page still updates.
              </Hint>
            </span>
            <div className="es-actionrow">
              <Input placeholder="https://api.cloudflare.com/client/v4/pages/webhooks/deploy_hooks/…"
                value={hook} onChange={(e) => setHook(e.target.value)}
                onBlur={() => { if (hook.trim() !== data.deployHookUrl) save.mutate({ deployHookUrl: hook.trim() }); }} />
            </div>
            <span className="es-hint">
              {!data.deployHookUrl
                ? 'Not set — changes reach the gallery page only until this client’s site is rebuilt by hand.'
                : data.deployPendingSince
                  ? `A change from ${ago(data.deployPendingSince)} is waiting for the next sweep.`
                  : data.deployFiredAt
                    ? `Up to date. Last build triggered ${ago(data.deployFiredAt)}.`
                    : 'Set, but nothing has changed since — no build triggered yet.'}
            </span>

            <span className="es-label">
              Cache refresh URL
              <Hint label="What is this for?">
                For a DNN site using the TritonIQ DNN Gallery module. Those pages
                are built on every visit from a copy of the gallery the site keeps
                for a few minutes; calling this URL on each save makes the site
                drop that copy, so the change shows on its next page view. Copy
                it from the module&rsquo;s Gallery settings, under Connection
                &mdash; it includes the site&rsquo;s secret.
              </Hint>
            </span>
            <div className="es-actionrow">
              <Input placeholder="https://www.example-client.com/API/TritonIQGallery/Gallery/Webhook?secret=…"
                value={cacheHook} onChange={(e) => setCacheHook(e.target.value)}
                onBlur={() => { if (cacheHook.trim() !== data.cacheHookUrl) save.mutate({ cacheHookUrl: cacheHook.trim() }); }} />
            </div>
            <span className="es-hint">
              {!data.cacheHookUrl
                ? 'Not set — a DNN site shows a save once its cached copy expires (10 minutes by default).'
                : data.cachePendingSince
                  ? `The save from ${ago(data.cachePendingSince)} has not reached the site yet; the next sweep retries.`
                  : data.cacheFiredAt
                    ? 'Up to date. The site was told about the latest save.'
                    : 'Set. It is called on the next save.'}
            </span>

            {save.isPending && <p className="es-hint">Saving…</p>}
          </>
        )}
        {err && <p className="vault-err"><Icon name="alert-circle" size={13} /> {err}</p>}
      </div>
      <div className="es-modal__foot">
        <Button variant="primary" size="sm" onClick={onClose}>Done</Button>
      </div>
    </Modal>
  );
}

export function GallerySettingsButton() {
  const isAdmin = useIsAdmin();
  const [open, setOpen] = useState(false);
  if (!isAdmin) return null;

  return (
    <>
      <Button variant="ghost" size="sm" iconLeft={<Icon name="settings" size={15} />} onClick={() => setOpen(true)}>
        Gallery settings
      </Button>
      {open && <SettingsDialog onClose={() => setOpen(false)} />}
    </>
  );
}
